<template>
  <!--ACCOUNT SUMMARY-->
  <el-card
      class="box-card span-12 gap_xs">
    <!-- ------------------------------------------ HEADER ------------------------------------------------- -->
    <div
        slot="header"
        class="clearfix">
      <span class="header-item">Account Summary</span>
      <span class="ring-card__header-buttons">
        <RefreshButton @click.native="refresh(true)" />
      </span>
    </div>

    <!-- -------------------------------------- CUSTOMER DETAILS --------------------------------------------- -->
    <div
        v-loading="accountSummary.loading"
        class="grid">
      <div
          data-col="basic-info"
          class="span-3 span-6-t span-12-p">
        <div class="customer-field">
          <div class="ring-card__input-label">
            Full Name
          </div>
          <div class="ring-card__input_dark_bg">
            {{ name }}
          </div>
        </div>
        <div class="customer-field">
          <div class="ring-card__input-label">
            Phone Number
          </div>
          <div class="ring-card__input_dark_bg">
            {{ phoneNumber || 'No number' }}
          </div>
        </div>
        <div class="customer-field">
          <div class="ring-card__input-label">
            Account Created On
          </div>
          <div class="ring-card__input_dark_bg">
            <time-zone-formatter :current-time="customer.created_at" />
          </div>
        </div>
      </div>

      <div
          data-col="email"
          class="span-3 span-6-t span-12p">
        <div class="customer-field">
          <div class="ring-card__input-label">
            Email
          </div>
          <div class="ring-card__input_dark_bg">
            {{ email }}
          </div>
        </div>
        <div
            v-acl.show="{permission: 'view', on: 'DCCS3'}"
            class="customer-field">
          <div class="ring-card__input-label">
            <span>Account Type</span>
          </div>
          <div class="ring-card__input_dark_bg">
            {{ accountTypeDescription }}
          </div>
        </div>
        <div
            v-if="customer.is_delayed_fulfillment && delayedFulfillmentEnabled"
            class="customer-field">
          <div class="ring-card__input-label">
            Fulfillment Status
          </div>
          <div class="ring-card__input_dark_bg">
            <el-popover
                :disabled="!showFulfillmentReason"
                placement="right"
                trigger="hover">
              <span
                  slot="reference"
                  class="delayed-color">Delayed</span>
              <FulfillmentReasons :reason="delayedFulfillmentReason" />
            </el-popover>
          </div>
        </div>

        <!-- -------------------------------------- ARID DIALOG BOX --------------------------------------------- -->

        <el-dialog
            :visible.sync="arid.logsDialogVisible"
            width="50%"
            title="Authorize Account">
          <div class="modal-body">
            <el-card>
              <h3>Account Recovery Attempts</h3>
              <el-table
                  :data="arid.visibleLogs"
                  empty-text="No ARID attempts.">
                <el-table-column
                    prop="inquiryStatus"
                    label="Attempt" />
                <el-table-column
                    prop="created_at"
                    label="Created at">
                  <template slot-scope="scope">
                    <time-zone-formatter
                        :current-time="scope.row.createdAt" />
                  </template>
                </el-table-column>
                <el-table-column
                    prop="declineReasons"
                    label="Reason">
                  <template slot-scope="scope">
                    <span
                        v-for="(reason,index) in scope.row.declineReasons"
                        :key="reason.key">
                      {{ reason.text | sentenceCase }}<span v-if="index < scope.row.declineReasons.length-1">,</span><br>
                    </span>
                  </template>
                </el-table-column>
              </el-table>
              <el-pagination
                  v-show="arid.allLogs.length > arid.pageSize"
                  align="right"
                  :current-page.sync="arid.currentPage"
                  :page-size="arid.pageSize"
                  small
                  layout="prev, pager, next"
                  :total="arid.allLogs.length"
                  @current-change="changeAridLogsPage" />

              <p style="margin-top: 10px; word-break: break-word;">
                {{ arid.attempts.remaining }} of {{ arid.attempts.total }} attempts remaining.

                <span v-if="arid.attempts.remaining > 0">
                  Ask the neighbor to attempt to login to the Ring App and select <b>Try another way</b>. The team member can also use the Kustomer shortcut <b>ARID</b> to email the neighbor a link to attempt Account Recovery.
                </span>
                <span v-if="arid.attempts.remaining == 0">
                  Manually verify the customer's account.
                </span>
              </p>
              <p style="word-break: break-word;">
                For more information on <b>account recovery</b>, refer to <a
                    href="https://ring.com/support/internal/articles/9ava9/two-step-verification-2sv"
                    target="_blank">this article</a> in Shelf.<br>
                For more information on <b>ARID</b>, refer to <a
                    href="https://ring.com/support/internal/articles/ax08m/account-recovery-identification-arid-information"
                    target="_blank">this article</a> in Shelf.
              </p>
            </el-card>
          </div>
          <span
              slot="footer"
              class="dialog-footer">
            <el-button
                type="primary"
                @click="showAuthorizationLogin">
              Manually Verify
            </el-button>
            <el-button
                @click="arid.logsDialogVisible = false">
              Close
            </el-button>
          </span>
        </el-dialog>

        <el-dialog
            :visible.sync="authorizationLoginModal"
            width="50%"
            title="Generate Ring Sign-In Code"
            @open="checked = false">
          <div class="modal-body">
            <span
                v-if="!otp">
              <input
                  id="otp_verified_identity"
                  v-model="checked"
                  type="checkbox">
              <label
                  for="otp_verified_identity">
                I have verified the neighbors identity and confirmed they are authorized to login to the account
              </label>
            </span>
            <div
                v-if="otp"
                style="font-size: xx-large; text-align: center;">
              {{ otp }}
            </div>
          </div>
          <span
              slot="footer"
              class="dialog-footer">
            <span v-if="!otp">
              <el-button
                  type="primary"
                  :disabled="!checked"
                  @click="getCCOTP()">
                Generate Ring Sign-In Code
              </el-button>
              <el-button
                  @click="authorizationLoginModal=false">
                Cancel
              </el-button>
            </span>
            <span v-if="otp">
              <el-button
                  type="primary"
                  @click="closeModal()">Close</el-button>
            </span>
          </span>
        </el-dialog>

        <!-- ACTION MENU - ACCOUNT VERIFICATION -->
        <div
            v-if="originalRequestAuthForm"
            class="ring-card-action-box grid gap-no">
          <div class="span-1 center-verticle">
            <img
                :src="icons.accountRecovery"
                width="32">
          </div>
          <div class="span-8 center-verticle ring-card-action-box__text ring-card-action-box__title">
            Account Verification
          </div>
          <div class="span-3 button-container">
            <el-button
                :disabled="isLocked"
                size="small"
                type="primary"
                class="custom-el-button"
                plain
                @click="verifyRingCustomerAccount">
              Verify Account
            </el-button>
          </div>
        </div>
        <div
            v-if="originalRequestAuthForm"
            class="customer-field">
          <div class="ring-card__input-label"></div>
          <div class="ring-card__input">
            <el-alert
                v-if="emailVerification.code"
                title=""
                type="info"
                @close="emailVerification.code = null">
              <slot>
                <div class="flex flex-col">
                  <span>This is the code sent to</span>
                  <a
                      style="margin-bottom: 5px"
                      :href="mailTo">{{ email }}</a>
                  <span style="font-size: xx-large;">{{ emailVerification.code }}</span>
                </div>
              </slot>
            </el-alert>
          </div>
        </div>
      </div>

      <!-- ---------------------------------------- ACTION MENU ----------------------------------------------- -->

      <div
          data-col="actions"
          class="span-6 span-12-t span-12-p">
        <!-- ACTION MENU - ACCOUNT VERIFICATION -->
        <RingAuthorizationForm v-if="piiAuthRequestFormVisible" />

        <!-- ACTION MENU - ACCOUNT RECOVERY -->
        <!-- Opens ARID Dialog Box -->
        <div
            v-acl.show="{permission: 'authorizeLogin', on: 'Customer'}"
            class="ring-card-action-box grid gap-no">
          <div class="span-1 center-verticle">
            <img
                :src="icons.accountRecovery"
                width="32">
          </div>
          <div class="span-8 center-verticle ring-card-action-box__text ring-card-action-box__title ring-card-action-box__account-recovery">
            Account Recovery
          </div>
          <div
              class="span-3 button-container">
            <el-button
                slot="reference"
                :disabled="isLocked"
                size="small"
                type="primary"
                class="custom-el-button"
                plain
                @click="handleAuthorizationLogin">
              Recover Account
            </el-button>
          </div>
        </div>

        <!-- ACTION MENU - TWO-FACTOR AUTHENTICATION -->
        <div class="ring-card-action-box grid gap-no">
          <div
              style="height: 100%">
            <img
                :src="icons.twoFA"
                width="32">
          </div>
          <div class="span-8">
            <div class="ring-card-action-box__text ring-card-action-box__title">
              Two-Step Verification
            </div>
            <div v-if="tfaEnabled">
              <div class="ring-card-action-box__text ring-card-action-box__description">
                {{ tsvInfoMessage }}
              </div>
              <div class="ring-card-action-box__text ring-card-action-box__text-success">
                Turned on
              </div>
            </div>
            <div v-else>
              <div class="ring-card-action-box__text ring-card-action-box__description">
                2SV can only be turned on by the user.
              </div>
              <div class="ring-card-action-box__text ring-card-action-box__text-error">
                Turned off
              </div>
            </div>
          </div>
          <div class="span-3 button-container">
            <a
                href="https://ring.com/support/internal/articles/9ava9/two-step-verification-2sv"
                target="_blank">
              <el-button
                  v-if="!tfaEnabled"
                  slot="reference"
                  size="small"
                  type="primary"
                  class="custom-el-button"
                  plain>
                Help turn on 2SV
              </el-button>
            </a>
          </div>
        </div>
      </div>
    </div>
    <!--    Account Alerts-->
    <div v-if="!accountSummary.loading && hasAlerts">
      <label
          class="ring-card__input-label expanded-title"
          style="margin-bottom: 10px">Account Alerts</label>
      <tags
          tags-direction="row"
          :list="dynamicTags" />
    </div>
  </el-card>
</template>

<script>
import Tags from "@/components/Shared/Tags";
import { createNamespacedHelpers } from "vuex";
import { get, capitalize, isEmpty } from "lodash";
import axios from "axios";
import LockAccountButton from "@/components/Shared/Account/LockAccountButton";
import NewRingAuthForm from "@/containers/Customer/Authorizations/NewRingAuthForm";
import RefreshButton from "@/containers/Customer/RefreshButton";
import permission from "@/mixins/permission";
import RingAuthorizationForm from "../../NewUI/components/customer/authorizations/ring/RingAuthorizationForm";
import FulfillmentReasons from "@/components/Shared/FulfillmentReasons.vue";

const {
  mapGetters: accountGetters,
  mapActions: accountActions,
  mapMutations: accountMutations,
} = createNamespacedHelpers("db/account");
const { mapGetters: sharedGetters } = createNamespacedHelpers("shared");

export default {
  name: "NewAccountSummary",
  components: {
    RingAuthorizationForm,
    Tags,
    LockAccountButton,
    NewRingAuthForm,
    RefreshButton,
    FulfillmentReasons,
  },
  mixins: [permission],
  data() {
    return {
      icons: {
        accountRecovery: require("@/assets/icons/action-account-recovery.svg"),
        fraudDetection: require("@/assets/icons/action-fraud-detection.svg"),
        twoFA: require("@/assets/icons/action-2fa.svg"),
      },
      authorizationLoginModal: false,
      checked: false,
      otp: null,
      emailVerification: {
        code: null,
        countdown: "",
        working: false,
      },
      accountSummary: { loading: false },
      arid: {
        loadingLogs: false,
        logsDialogVisible: false,
        allLogs: [],
        currentPage: 1,
        pageSize: 4,
        visibleLogs: [],
        attempts: {
          remaining: 0,
          total: 4,
          retryDays: 0,
        },
      },
    };
  },
  computed: {
    ...accountGetters([
      "accountVerified",
      "customer",
      "customerId",
      "piiOff",
      "piiOn",
      "dccsOn",
      "dccsOff",
      "isLocked",
      "aridEnabled",
    ]),
    ...sharedGetters([
      "userHasPermission",
      "currentAuthorization",
      "delayedFulfillmentEnabled",
      "delayedFulfillmentTipsEnabled",
    ]),
    showFulfillmentReason() {
      return (
        this.delayedFulfillmentTipsEnabled &&
        this.customer.is_delayed_fulfillment
      );
    },
    delayedFulfillmentReason() {
      return this.customer.delayed_fulfillment_reason
        ? this.customer.delayed_fulfillment_reason
        : "";
    },
    originalRequestAuthForm() {
      return this.piiOff && this.dccsOff;
    },
    piiAuthRequestFormVisible() {
      return this.piiOn;
    },
    mailTo() {
      return `mailto:${this.email}`;
    },
    email() {
      return this.customer.email;
    },
    name() {
      return `${this.customer.first_name} ${this.customer.last_name}`;
    },
    phoneNumber() {
      return this.customer.phone_number;
    },
    isEmailVerified() {
      return this.customer.status === "verified" || this.customer.status === 2;
    },
    statusIconClass() {
      let css = (icon, color) =>
        `el-icon-circle-${icon} email-status-icon__${color}`;
      if (this.isEmailVerified) return css("check", "verified");
      else return css("close", "unverified");
    },
    status() {
      if (!this.customer.status) {
        return "Legacy";
      } else if (this.customer.status === 2) {
        return "Verified";
      } else if (this.customer.status === 1) {
        return "Unverified";
      } else {
        return capitalize(this.customer.status);
      }
    },
    hasAlerts() {
      return this.dynamicTags.length > 0;
    },
    dynamicTags() {
      if (!this.customer.hasOwnProperty("account_tags")) return [];
      return this.customer.account_tags;
    },
    tfaEnabled() {
      return Boolean(this.customer["tfa_enabled"]);
    },
    tfaPhone() {
      return this.customer["tfa_phone_number"];
    },
    tsvInfoMessage() {
      if (isEmpty(this.tfaPhone) || this.tfaPhone === "no phone") {
        return `Authenticator App`;
      }

      return `2SV Phone Number: ${this.tfaPhone}`;
    },
    accountTypeDescription() {
      if (get(this.customer, "account_type", null) === "amazon") return "LWA";
      else if (get(this.customer, "amazon_profile", null))
        return "Amazon-linked";
      else return "Ring";
    },
    verticalAlignment() {
      return "column-centered";
    },
  },
  watch: {
    "customer.id": function (id, previousId) {
      if (id !== previousId) {
        this.emailVerification.code = null;
        this.refresh(true);
      }
    },
  },
  mounted() {
    // Resetting first authorization request if new user is loaded.
    this.setIsFirstAuthorizationAttempt(true);
  },
  created() {
    if (this.accountSummary.loading === false) {
      this.refresh(true);
    }
  },
  methods: {
    get,
    ...accountMutations(["setTIV"]),
    ...accountActions([
      "globalSearch",
      "verifyRingCustomer",
      "fetchAuthorizationChannels",
      "updateCustomer",
      "generateCCOTP",
      "closeModal",
      "getActiveAuthorizationChannels",
      "setIsFirstAuthorizationAttempt",
    ]),
    refresh(keep_previous_data = false) {
      this.accountSummary.loading = true;

      this.getActiveAuthorizationChannels();

      this.globalSearch({
        criteria: this.customer.id,
        model: "Customer",
        includes: [
          "account_admin_tags",
          "account_robin_tags",
          "amazon_profile",
        ],
        keep_previous_data,
      })
        .catch((error) => {
          console.error("Error in GlobalSearch", error);
        })
        .finally(() => (this.accountSummary.loading = false));
    },
    handleAuthorizationLogin() {
      if (
        this.aridEnabled &&
        this.userHasPermission({ permission: "showAridLogs", on: "Customer" })
      ) {
        this.showAridLogs();
      } else {
        this.showAuthorizationLogin();
      }
    },
    showAuthorizationLogin() {
      this.arid.logsDialogVisible = false;
      this.authorizationLoginModal = true;
      this.otp = null;
    },
    showAridLogs() {
      this.arid.loadingLogs = true;
      axios
        .get(`v2/customers/${this.customer.id}/arid_logs.json`)
        .then((response) => {
          this.arid.allLogs = response.data;
          this.arid.allLogs.forEach((log) => {
            log.declineReasons = (log.declineReasons || []).map(
              (reason, index) => {
                return { text: reason, key: index };
              }
            );
          });
          this.arid.visibleLogs =
            this.arid.allLogs.length > this.arid.pageSize
              ? this.arid.allLogs.slice(0, this.arid.pageSize)
              : this.arid.allLogs;
          this.arid.logsDialogVisible = true;
          this.arid.currentPage = 1;

          this.arid.attempts.remaining = this.arid.attempts.total;
          let i = 0;
          while (
            i < this.arid.allLogs.length &&
            this.arid.attempts.remaining > 0
          ) {
            const log = this.arid.allLogs[i];
            if (log.inquiryStatus === "APPROVED") break;

            const createdAt = this.$moment(log.createdAt);
            const diffDays = this.$moment().diff(createdAt, "days");
            if (diffDays > 60) break;

            i++;
            if (log.inquiryStatus === "DECLINED") {
              this.arid.attempts.remaining--;
            }
          }

          this.arid.attempts.retryDays = 0;
          if (this.arid.allLogs.length) {
            this.arid.attempts.retryDays = this.$moment(
              this.arid.allLogs[0].createdAt
            )
              .add(60, "days")
              .diff(this.$moment(), "days");
          }
        })
        .catch(() => this.showAuthorizationLogin())
        .finally(() => (this.arid.loadingLogs = false));
    },
    changeAridLogsPage(page) {
      this.arid.visibleLogs = this.arid.allLogs.slice(
        (page - 1) * this.arid.pageSize,
        (page - 1) * this.arid.pageSize + this.arid.pageSize
      );
    },
    verifyRingCustomerAccount() {
      this.verifyRingCustomer({ customerId: this.customerId })
        .then((response) => {
          this.emailVerification.code = response.code;
        })
        .catch((_) => {
          this.$message.error("Email couldn't be sent");
        });
    },
    getCCOTP() {
      this.generateCCOTP({ customerId: this.customerId })
        .then((response) => {
          this.otp = response.ccotp;
        })
        .catch((_) => {
          this.$message.error("Authorize Login couldn't be sent");
        });
    },
    closeModal() {
      this.otp = null;
      this.authorizationLoginModal = false;
    },
  },
};
</script>

<style  lang="scss" scoped>
.delayed-color {
  cursor: help;
  color: $--color-milano-red;
}

.location-section:first-child label {
  margin-bottom: 10px;
}

.location-section:nth-child(n + 2) label {
  display: none;
}
.email-status-icon__verified {
  color: $--color-fun-green;
  padding-left: 7px;
}

.email-status-icon__unverified {
  color: $--color-milano-red;
  padding-left: 7px;
}
.customer-field {
  margin-bottom: 20px;
  margin-top: 10px;
}
.cancellation-pending-warning-icon {
  color: $--color-flamingo;
  padding: 7px;
}

p.cancellation-pending-warning-text {
  font-size: $--font-size-small;
  color: $--color-flamingo;
  text-align: left;
  word-break: break-word;
}

.el-dropdown-link {
  cursor: pointer;
  color: #409eff;
}
.el-icon-arrow-down {
  font-size: 12px;
}

.center-verticle {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.column-centered {
  display: flex;
  justify-content: center;
}

.button-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
}

.ring-card-action-box {
  display: grid;
  align-items: center;
  min-height: 80px;
}

/* Ensure all buttons have the same width */
.custom-el-button {
  width: 150px !important;
  min-width: 150px !important;
}

/* Fix for the Help turn on 2SV button inside an anchor tag */
.button-container a .custom-el-button {
  width: 150px !important;
  min-width: 150px !important;
}
</style>
